import { CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AnimatedCard } from '@/components/ui/animated-card';
import { GlowButton } from '@/components/ui/glow-button';
import { 
  Plus, 
  Clock, 
  DollarSign, 
  BarChart, 
  Users, 
  Activity,
  ExternalLink,
  Bell,
  Zap,
  Settings
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QuickAction } from '@/services/dashboard.service';
import { useNavigate } from 'react-router-dom';

interface QuickActionsProps {
  actions: QuickAction[];
  isLoading?: boolean;
  className?: string;
}

const actionIcons = {
  Plus,
  Clock,
  DollarSign,
  BarChart,
  Users,
  Activity,
} as const;

export function QuickActions({ actions, isLoading = false, className }: QuickActionsProps) {
  const navigate = useNavigate();

  const handleActionClick = (action: QuickAction) => {
    navigate(action.href);
  };

  if (isLoading) {
    return (
      <AnimatedCard 
        gradient={true} 
        glow={true} 
        className={cn("shadow-brand-sm hover:shadow-brand-md transition-all duration-500 p-0", className)}
      >
        <CardHeader className="relative">
          <div className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary animate-pulse" />
            <CardTitle className="gradient-text-primary">Quick Actions</CardTitle>
          </div>
          <CardDescription className="text-muted-foreground/80">
            Common tasks and operations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <div 
              key={i} 
              className="h-14 bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 rounded-xl shimmer border border-slate-100/50"
            />
          ))}
        </CardContent>
      </AnimatedCard>
    );
  }

  const getActionStyle = (action: QuickAction) => {
    if (action.urgent) {
      return {
        bg: 'bg-gradient-to-r from-orange-500/10 to-amber-500/5',
        border: 'border-orange-200/50 hover:border-orange-300/70',
        iconBg: 'bg-gradient-to-br from-orange-500/20 to-amber-500/10',
        iconColor: 'text-orange-600',
        badgeBg: 'bg-gradient-to-r from-orange-500/20 to-amber-500/10',
        badgeText: 'text-orange-700',
        shadow: 'hover:shadow-orange-500/20'
      };
    }
    return {
      bg: 'bg-gradient-to-r from-slate-50/50 to-transparent',
      border: 'border-slate-200/50 hover:border-primary/30',
      iconBg: 'bg-gradient-to-br from-primary/10 to-secondary/5',
      iconColor: 'text-primary',
      badgeBg: 'bg-gradient-to-r from-primary/10 to-secondary/5',
      badgeText: 'text-primary',
      shadow: 'hover:shadow-primary/20'
    };
  };

  return (
    <AnimatedCard 
      gradient={true} 
      glow={true}
      className={cn(
        "shadow-brand-sm hover:shadow-brand-lg transition-all duration-500 p-0 group relative overflow-hidden",
        className
      )}
    >
      {/* Enhanced background */}
      <div className="absolute inset-0 bg-gradient-to-br from-secondary/5 via-transparent to-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      
      <CardHeader className="relative z-10">
        <CardTitle className="flex items-center gap-2 group-hover:gradient-text-primary transition-all duration-300">
          <Zap className="h-5 w-5 text-primary group-hover:scale-110 transition-transform" />
          Quick Actions
        </CardTitle>
        <CardDescription className="text-muted-foreground/80 group-hover:text-muted-foreground transition-colors">
          Common tasks and operations
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-3 relative z-10">
        {actions.map((action) => {
          const Icon = actionIcons[action.icon as keyof typeof actionIcons] || ExternalLink;
          const style = getActionStyle(action);
          
          return (
            <GlowButton
              key={action.id}
              variant="ghost"
              className={cn(
                "w-full justify-start h-auto p-4 group/action transition-all duration-300 border",
                style.bg,
                style.border,
                style.shadow,
                "hover:shadow-brand-sm"
              )}
              onClick={() => handleActionClick(action)}
            >
              <div className={cn(
                "flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center border border-white/50 transition-all duration-300 group-hover/action:scale-110 group-hover/action:shadow-sm",
                style.iconBg
              )}>
                <Icon className={cn("h-5 w-5", style.iconColor)} />
              </div>
              
              <div className="flex-1 text-left min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-900 text-sm group-hover/action:gradient-text-primary transition-all duration-300 truncate">
                    {action.title}
                  </p>
                  {action.count !== undefined && action.count > 0 && (
                    <span className={cn(
                      "flex-shrink-0 inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border transition-all duration-300 group-hover/action:scale-105",
                      style.badgeBg,
                      style.badgeText,
                      action.urgent ? "border-orange-200/50" : "border-primary/20"
                    )}>
                      {action.urgent && <Bell className="w-3 h-3 mr-1 animate-pulse flex-shrink-0" />}
                      {action.count}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600 mt-1 group-hover/action:text-slate-700 transition-colors">
                  {action.description}
                </p>
              </div>
            </GlowButton>
          );
        })}
        
        <div className="pt-4 border-t gradient-border-sm mt-6">
          <GlowButton 
            variant="outline-soft" 
            size="sm"
            className="w-full group/more"
            onClick={() => navigate('/settings')}
          >
            <Settings className="w-4 h-4 mr-2 group-hover/more:rotate-90 transition-transform duration-300" />
            More Settings
            <ExternalLink className="w-3 h-3 ml-2 group-hover/more:translate-x-1 transition-transform" />
          </GlowButton>
        </div>
      </CardContent>
    </AnimatedCard>
  );
}
